import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Mail, CheckCircle2, XCircle } from "lucide-react";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const token = searchParams.get("token");
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"initial" | "success" | "error" | "resubscribed" | "invalid">("initial");
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  // Validate token on mount using security definer function
  useEffect(() => {
    const validateToken = async () => {
      if (!email || !token) {
        setStatus("invalid");
        setTokenValid(false);
        return;
      }

      try {
        // Call the secure validation function
        const { data, error } = await supabase.rpc("validate_unsubscribe_token", {
          _email: email,
          _token: token,
        });

        if (error) {
          console.error("Error validating token:", error);
          setStatus("invalid");
          setTokenValid(false);
          toast({
            title: "Invalid Link",
            description: "This unsubscribe link is invalid or has expired.",
            variant: "destructive",
          });
        } else if (data === true) {
          setTokenValid(true);
        } else {
          setStatus("invalid");
          setTokenValid(false);
          toast({
            title: "Invalid Link",
            description: "This unsubscribe link is invalid or has expired.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error validating token:", error);
        setStatus("invalid");
        setTokenValid(false);
      }
    };

    validateToken();
  }, [email, token, toast]);

  const handleUnsubscribe = async () => {
    if (!email || !token || !tokenValid) {
      toast({
        title: "Error",
        description: "Invalid unsubscribe request",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Call the secure process_unsubscribe function
      const { data, error } = await supabase.rpc("process_unsubscribe", {
        _email: email,
        _token: token,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; message?: string };

      if (result.success) {
        setStatus("success");
        toast({
          title: "Unsubscribed Successfully",
          description: "You've been removed from our mailing list.",
        });
      } else {
        setStatus("error");
        toast({
          title: "Error",
          description: result.error || "Failed to unsubscribe. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error unsubscribing:", error);
      setStatus("error");
      toast({
        title: "Error",
        description: "Failed to unsubscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    if (!email) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from("email_subscribers")
        .update({ is_active: true })
        .eq("email", email);

      if (error) throw error;

      setStatus("resubscribed");
      toast({
        title: "Resubscribed Successfully",
        description: "You're back on our mailing list!",
      });
    } catch (error: any) {
      console.error("Error resubscribing:", error);
      toast({
        title: "Error",
        description: "Failed to resubscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
            {status === "invalid" && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                    <XCircle className="w-8 h-8 text-destructive" />
                  </div>
                </div>
                <h1 className="text-3xl font-bold text-center mb-4">
                  Invalid Unsubscribe Link
                </h1>
                <p className="text-muted-foreground text-center mb-6">
                  This unsubscribe link is invalid, has expired, or has already been used. Unsubscribe links expire after 30 days for your security.
                </p>
                <div className="text-center">
                  <Link to="/">
                    <Button variant="default">
                      Return to Homepage
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === "initial" && tokenValid && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-accent" />
                  </div>
                </div>
                <h1 className="text-3xl font-bold text-center mb-4">
                  Unsubscribe from Emails
                </h1>
                <p className="text-muted-foreground text-center mb-6">
                  We're sorry to see you go! If you unsubscribe, you won't receive updates about new security resources and tips from 4FOX Security.
                </p>
                {email && (
                  <div className="bg-muted p-4 rounded-lg mb-6">
                    <p className="text-sm text-center">
                      <span className="font-semibold">Email:</span> {email}
                    </p>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={handleUnsubscribe}
                    disabled={loading || !email || !tokenValid}
                    variant="destructive"
                    className="w-full sm:w-auto"
                  >
                    {loading ? "Processing..." : "Unsubscribe"}
                  </Button>
                  <Link to="/">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      Keep Subscription
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === "success" && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                <h1 className="text-3xl font-bold text-center mb-4">
                  Successfully Unsubscribed
                </h1>
                <p className="text-muted-foreground text-center mb-6">
                  You've been removed from our mailing list. You won't receive any further emails from us.
                </p>
                <div className="bg-muted p-6 rounded-lg mb-6">
                  <h2 className="font-semibold mb-2 text-center">Changed your mind?</h2>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    You can resubscribe at any time to receive security tips and resources.
                  </p>
                  <div className="flex justify-center">
                    <Button
                      onClick={handleResubscribe}
                      disabled={loading}
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      {loading ? "Processing..." : "Resubscribe"}
                    </Button>
                  </div>
                </div>
                <div className="text-center">
                  <Link to="/">
                    <Button variant="default">
                      Return to Homepage
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === "resubscribed" && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-accent" />
                  </div>
                </div>
                <h1 className="text-3xl font-bold text-center mb-4">
                  Welcome Back!
                </h1>
                <p className="text-muted-foreground text-center mb-6">
                  You've been successfully resubscribed. You'll continue to receive our security resources and updates.
                </p>
                <div className="text-center">
                  <Link to="/">
                    <Button variant="default">
                      Return to Homepage
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === "error" && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                    <XCircle className="w-8 h-8 text-destructive" />
                  </div>
                </div>
                <h1 className="text-3xl font-bold text-center mb-4">
                  Oops! Something Went Wrong
                </h1>
                <p className="text-muted-foreground text-center mb-6">
                  We encountered an error processing your request. Please try again or contact us for assistance.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => setStatus("initial")}
                    variant="default"
                    className="w-full sm:w-auto"
                  >
                    Try Again
                  </Button>
                  <Link to="/">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      Return to Homepage
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Unsubscribe;
